import { REAL_TIME_HANDLER_TYPE } from 'app/automations/automation-helpers-actions';
import { FormSettingEntityProps, FormSettings, FormSettingsHandlerTypes } from 'app/campaign/widgets.model';
import { HandlerRealTimeProp } from './handler-real-time-prop';
import { RealTimeHandlerEntry } from './real-time-handler-entry';

export const HANDLER_ORDER: ReadonlyArray<FormSettingsHandlerTypes> = [
  FormSettingsHandlerTypes.Success,
  FormSettingsHandlerTypes.ValidationError,
  FormSettingsHandlerTypes.ConflictError,
  FormSettingsHandlerTypes.UnknownError,
];

type HandlerView = Record<string, RealTimeHandlerEntry>;
type Translate = (key: string) => string;

/** For the success outcome only, "no step and no message" means `Close`. */
export function buildHandlerView(
  formSettings: FormSettings | null | undefined,
  submitSwitchStateId: number | null,
  translate: Translate,
): HandlerView {
  const view: HandlerView = {};

  if (formSettings?.success) {
    const clone: Record<string, RealTimeHandlerEntry> = JSON.parse(JSON.stringify(formSettings));
    for (const key of Object.keys(clone)) {
      const entry = clone[key];
      if (!entry || typeof entry !== 'object') {
        continue;
      }
      if (key === FormSettingsHandlerTypes.Success && !entry.stepId && submitSwitchStateId) {
        entry.stepId = submitSwitchStateId;
      }
      entry.defaultMessage = translate('defaultRealTimeMessage.' + key);
      entry.action =
        key === FormSettingsHandlerTypes.Success && !entry.stepId && !entry.message
          ? HandlerRealTimeProp.Close
          : entry.stepId
            ? HandlerRealTimeProp.StepId
            : HandlerRealTimeProp.Message;
      view[key] = entry;
    }
    return view;
  }

  for (const key of HANDLER_ORDER) {
    const defaultMessage = translate('defaultRealTimeMessage.' + key);
    const isSuccessWithStep = key === FormSettingsHandlerTypes.Success && !!submitSwitchStateId;
    view[key] = {
      action: isSuccessWithStep ? HandlerRealTimeProp.StepId : HandlerRealTimeProp.Message,
      message: defaultMessage,
      stepId: isSuccessWithStep ? submitSwitchStateId : null,
      defaultMessage,
      $type: REAL_TIME_HANDLER_TYPE,
    };
  }
  return view;
}

export function applyHandlerActionChange(entry: RealTimeHandlerEntry): void {
  if (entry.action === HandlerRealTimeProp.Message) {
    entry.message = entry.message || entry.defaultMessage;
    entry.stepId = null;
  } else if (entry.action === HandlerRealTimeProp.StepId) {
    entry.message = null;
  } else {
    entry.message = null;
    entry.stepId = null;
  }
}

export function markInvalidSteps(view: HandlerView, choosableStateIds: ReadonlySet<number>): void {
  for (const entry of Object.values(view)) {
    if (entry.stepId) {
      entry.isStepInvalid = !choosableStateIds.has(entry.stepId);
    }
  }
}

export function isHandlerSaveDisabled(view: HandlerView, choosableStateIds: ReadonlySet<number>): boolean {
  return Object.values(view).some(
    (entry) => entry.action === HandlerRealTimeProp.StepId && (!entry.stepId || !choosableStateIds.has(entry.stepId)),
  );
}

/**
 * The view-only `action` / `defaultMessage` / `isStepInvalid` fields are dropped on save. `dataSourceId` is
 * preserved untouched — the leaderboard link is owned by a separate section, so this save must not clear it.
 */
export function toFormSettingsPayload(
  view: HandlerView,
  existingDataSourceId: string | null | undefined,
): FormSettings {
  const payload: Record<string, FormSettingEntityProps | string | null | undefined> = {
    dataSourceId: existingDataSourceId ?? null,
  };
  for (const key of Object.keys(view)) {
    const entry = view[key];
    if (!entry?.action) {
      continue;
    }
    payload[key] = {
      $type: REAL_TIME_HANDLER_TYPE,
      message: entry.action === HandlerRealTimeProp.Message ? (entry.message ?? entry.defaultMessage ?? '') : null,
      stepId: entry.action === HandlerRealTimeProp.StepId ? (entry.stepId ?? null) : null,
    } as FormSettingEntityProps;
  }
  return payload as unknown as FormSettings;
}
